import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Paperclip,
  Plus,
  RefreshCw,
  CheckCircle2,
  FileText,
  Music,
  Camera,
  Image as ImageIcon,
  Download
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const AttachmentsSection = () => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAttachment, setNewAttachment] = useState({
    filename: "",
    file_url: "",
    file_type: "image",
    description: ""
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/attachments`);
      setAttachments(res.data);
    } catch (error) {
      console.error("Error loading attachments:", error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleAddAttachment = async () => {
    if (!newAttachment.filename || !newAttachment.file_url) return;
    try {
      await axios.post(`${API}/attachments`, newAttachment);
      setNewAttachment({ filename: "", file_url: "", file_type: "image", description: "" });
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error("Error adding attachment:", error);
    }
  };

  const fileTypes = [
    { value: "image", label: "Image", icon: ImageIcon },
    { value: "document", label: "Document", icon: FileText },
    { value: "video", label: "Video", icon: Camera },
    { value: "audio", label: "Audio", icon: Music },
    { value: "other", label: "Other", icon: Paperclip }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500">
              <Paperclip className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Attachments</h2>
              <p className="text-sm text-slate-500">Manage uploaded files and documents</p>
            </div>
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={loadData}
              className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </motion.button>
            <motion.button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4" />
              Add Attachment
            </motion.button>
          </div>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200"
          >
            <h3 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              New Attachment
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <input
                className="border-2 border-purple-200 rounded-xl px-4 py-3 focus:border-purple-400 transition-all outline-none"
                placeholder="Filename"
                value={newAttachment.filename}
                onChange={e => setNewAttachment(prev => ({ ...prev, filename: e.target.value }))}
              />
              <select
                className="border-2 border-purple-200 rounded-xl px-4 py-3 focus:border-purple-400 transition-all outline-none"
                value={newAttachment.file_type}
                onChange={e => setNewAttachment(prev => ({ ...prev, file_type: e.target.value }))}
              >
                {fileTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <input
                className="border-2 border-purple-200 rounded-xl px-4 py-3 focus:border-purple-400 transition-all outline-none md:col-span-2"
                placeholder="File URL"
                value={newAttachment.file_url}
                onChange={e => setNewAttachment(prev => ({ ...prev, file_url: e.target.value }))}
              />
              <input
                className="border-2 border-purple-200 rounded-xl px-4 py-3 focus:border-purple-400 transition-all outline-none md:col-span-2"
                placeholder="Description (optional)"
                value={newAttachment.description}
                onChange={e => setNewAttachment(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <motion.button
              onClick={handleAddAttachment}
              className="mt-4 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <CheckCircle2 className="w-5 h-5" />
              Save Attachment
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Attachments Grid */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4">All Attachments ({attachments.length})</h3>
        
        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <Paperclip className="w-10 h-10 text-slate-300" />
            </div>
            <p className="text-slate-600 font-medium">No attachments yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {attachments.map((att, index) => {
              const typeInfo = fileTypes.find(t => t.value === att.file_type) || fileTypes[4];
              const TypeIcon = typeInfo.icon;
              return (
                <motion.div
                  key={att.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-purple-50 border border-slate-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500">
                      <TypeIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{att.filename}</p>
                      <p className="text-sm text-slate-600">{typeInfo.label}</p>
                      {att.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{att.description}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <a
                      href={att.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-3 py-2 rounded-xl bg-purple-100 text-purple-700 text-sm font-medium text-center hover:bg-purple-200 transition-colors"
                    >
                      <Download className="w-4 h-4 inline mr-1" />
                      Download
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentsSection;
